import React, { useState, useEffect, useRef } from 'react';
import './Experience.css';

const TimelineItem = ({ title, subtitle, children, isLeft, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.3, rootMargin: '-100px 0px -100px 0px' }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={itemRef}
      className={`timeline-item ${isLeft ? 'left' : 'right'} ${isVisible ? 'visible' : ''}`}
    >
      <div className="timeline-marker"></div>
      <div className="timeline-content">
        <div className="timeline-header">
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <ul className="timeline-details">
          {children}
        </ul>
      </div>
    </div>
  );
};

const Experience = () => {
  const experiences = [
    {
      title: "Chief Marketing Officer",
      subtitle: "Collegebase (Collegebase.org), July 2025 – Present",
      bullets: [
        "Scaled platform from 0 → 60K+ users through content-led growth, UGC, student community, and referral loops",
        "Recruited and onboarded 100+ UGC creators for Collegebase; 10+ consistently active, 20+ still posting regularly.",
        "Grew Collegebase social channels through daily founder-style content on LinkedIn documenting the build-in-public journey; generated $3K+ in early revenue",
        "Spearheaded partnerships with other edtech startups (Snowday) and education creators (@rakhimoff_amir, @seoul.pearson, etc.)"
      ]
    },
    {
      title: "UGC Content Creator",
      subtitle: "Cluely, July 2025 – Present",
      bullets: [
        "One of the first ~30 creators to become an official Cluely creator after passing their trial",
        "Generated 2M+ views across content; tested 10+ content styles to identify viral formats",
        "Created 40 videos in the first 30 days, helping shape early creator strategy and platform content standards"
      ]
    },
    {
      title: "FOCUS Program Participant",
      subtitle: "Jane Street Capital, May 2025",
      bullets: [
        "Selected to participate in a selective multi-day program exploring quantitative finance and problem-solving at Jane Street's NYC office.",
        "Collaborated with peers on trading simulations and logic puzzles while gaining insights from industry professionals.",
        "Learned about Jane Street's culture, career paths, and internship opportunities through immersive workshops and mentorship."
      ]
    },
    {
      title: "Events Director and Y/Labs Project Co-Lead (formerly Catalyst Program Mentee)",
      subtitle: "Yale Computer Society, Sep 2024 - Present",
      bullets: [
        "Organize and direct events for the Yale Computer Society, fostering community and professional development among members.",
        "Co-lead the Y/Labs project, a platform showcasing Yale labs and technical projects, overseeing development and outreach.",
        "Previously participated in the Catalyst Program, co-designed and built a budget tracker website using React.js, and delivered a functional final product showcased to program mentors."
      ]
    },
    {
      title: "Communications Chair (formerly First-Year Liaison)",
      subtitle: "Yale ColorStack, Sep 2024 - Present",
      bullets: [
        "Lead communications and outreach efforts to connect underrepresented students with tech industry resources.",
        "Organize workshops and networking sessions to foster diversity in computer science.",
        "Increase program engagement and attendance through effective communication and outreach."
      ]
    },
    {
      title: "Social Media Manager",
      subtitle: "Low Strung Cellos, Sep 2024 - Present",
      bullets: [
        "Curate and edit performance footage and photos into high-quality content, posting biweekly across platforms.",
        "Grow Low Strung's reach by 100x, amplifying audience engagement and expanding the group's recognition."
      ]
    },
    {
      title: "Discovery Day Participant",
      subtitle: "Susquehanna International Group, Feb 2025",
      bullets: [
        "Engaged in an exclusive one-day program learning about quantitative trading, financial markets, and SIG's approach to decision-making."
      ]
    },
    {
      title: "Georgia Recruitment Director",
      subtitle: "Do Re Mi Project Inc., Aug 2023 - May 2024",
      bullets: [
        "Taught young cellists the fundamentals of playing the cello and advertised the Do Re Mi Project to my community.",
        "Led weekly virtual lessons and contributed to monthly recruitment meetings.",
        "Helped students improve their cello skills and increased awareness of the Do Re Mi Project in Georgia."
      ]
    },
    {
      title: "Physics Research Intern",
      subtitle: "Kennesaw State University Department of Physics, Aug 2023 - Dec 2023",
      bullets: [
        "Analyzed the nucleation of lithium disilicate during crystallization via differential thermal analysis.",
        "Conducted physics research 12 hours/week at the Kennesaw State University Glass Laboratory.",
        "Presented findings on nucleation analysis to peers at Wheeler Magnet and Yale Engineering and Science Weekend."
      ]
    },
    {
      title: "MITES Semester Scholar",
      subtitle: "MIT Introduction to Technology, Engineering, and Science, Jun 2023 - Dec 2023",
      bullets: [
        "Selected as 1 of 230 from an applicant pool of over 2700.",
        "Completed a quantum computation course tested on Caltech undergraduate students.",
        "Presented a project on the superdense coding algorithm at a virtual symposium and wrote an article on superconducting qubits and quantum materials after interviewing researchers at MIT and Yale."
      ]
    },
    {
      title: "Mathematics Tutor",
      subtitle: "Mathnasium - The Math Learning Center, Jan 2023 - Jun 2023",
      bullets: [
        "Tutored elementary, middle, and high school students in mathematics.",
        "Utilized the Mathnasium Method to diagnose and fill knowledge gaps.",
        "Helped multiple students catch up to their grade level in math."
      ]
    }
  ];

  return (
    <div className="experience">
      <div className="notion-page">
        <div className="notion-page-header">
          <h1 className="notion-page-title">Experience</h1>
        </div>
      </div>

      <div className="timeline-container">
        <div className="timeline-line"></div>
        {experiences.map((exp, index) => (
          <TimelineItem
            key={index}
            title={exp.title}
            subtitle={exp.subtitle}
            isLeft={index % 2 === 0}
            index={index}
          >
            {exp.bullets.map((bullet, i) => (
              <li key={i}>{bullet}</li>
            ))}
          </TimelineItem>
        ))}
      </div>
    </div>
  );
};

export default Experience;
